import styled from "@emotion/styled";
import { ImpactShopSortOption, ImpactType } from "@ecologi/shared";
import { Typography } from "../Typography";
import { Select } from "../Select/Select";
import { ImpactShopFilter, ImpactShopFilterType } from "./useImpactShopFilter";
import {
  CategoryFilterValue,
  IMPACT_CATEGORY_OPTIONS,
  ALL_CATEGORIES_VALUE,
  ALL_CARBON_VALUE,
  ALL_LOCATIONS_VALUE,
  ALL_LOCATIONS_LABEL,
  SORT_OPTIONS,
} from "./impactShopFilters.constants";
import CloseIcon from "@ecologi/assets/icons/close-icon.svg";

const FilterBar = styled.div<{ hasActiveFilters: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 16px
    ${({ hasActiveFilters }) => (hasActiveFilters ? "6px" : "30px")};
  background-color: ${({ theme }) => theme.colors.mist};

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    padding: 8px 24px
      ${({ hasActiveFilters }) => (hasActiveFilters ? "6px" : "30px")};
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;

  @media ${({ theme }) => theme.bp.tabletUp} {
    flex-wrap: nowrap;
    width: auto;
  }

  > div:nth-of-type(1) {
    width: 100%;

    @media ${({ theme }) => theme.bp.mobileUp} {
      width: 200px;
    }
  }

  > div:nth-of-type(2) {
    width: 100%;

    @media ${({ theme }) => theme.bp.mobileUp} {
      width: 240px;
    }
  }

  button {
    background-color: ${({ theme }) => theme.colors.snowdrop} !important;

    p {
      font-size: 16px !important;
    }
  }

  ul li p {
    font-size: 16px !important;
  }
`;

const Label = styled(Typography)`
  display: none;
  color: ${({ theme }) => theme.colors.charcoal};
  white-space: nowrap;
  margin-bottom: 8px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    display: block;
  }
`;

const SortWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media ${({ theme }) => theme.bp.tabletUp} {
    width: auto;
  }

  > div {
    width: 100%;

    @media ${({ theme }) => theme.bp.mobileUp} {
      width: 200px;
    }
  }

  button {
    background-color: ${({ theme }) => theme.colors.snowdrop} !important;

    p {
      font-size: 16px !important;
    }
  }

  ul li p {
    font-size: 16px !important;
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 40px 16px;
  flex-wrap: wrap;

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    padding: 8px 24px 40px 24px;
  }
`;

const ActiveFiltersLabel = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoal};
  white-space: nowrap;
`;

const FilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  border: 1px solid ${({ theme }) => theme.colors.charcoalLight};
`;

const ChipText = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoal};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.charcoal};
  transition: opacity 0.2s ease;
  height: 18px;
  line-height: 0;

  &:hover {
    opacity: 0.7;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.charcoal};
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.charcoal};
  text-decoration: underline;
  transition: opacity 0.2s ease;
  font-family: inherit;
  font-size: inherit;

  &:hover {
    opacity: 0.7;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.charcoal};
    outline-offset: 2px;
  }
`;

export const ImpactShopFilterBar = ({
  selectedImpactTypes,
  selectedLocations,
  allLocations,
  availableLocations,
  availableCategories,
  sortOption,
  onCategoryChange,
  onLocationChange,
  setSortOption,
  removeFilter,
  clearAllFilters,
}: {
  selectedImpactTypes: CategoryFilterValue[];
  selectedLocations: string[];
  allLocations: string[];
  availableLocations: string[];
  availableCategories: CategoryFilterValue[];
  sortOption: ImpactShopSortOption;
  onCategoryChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  setSortOption: (sortOption: ImpactShopSortOption) => void;
  removeFilter: (filter: ImpactShopFilter | ImpactShopFilter[]) => void;
  clearAllFilters: () => void;
}) => {
  const isAllCarbonAvailable =
    availableCategories.includes(ImpactType.CARBON_OFFSETS) ||
    availableCategories.includes(ImpactType.CARBON_REMOVAL);

  const categoryOptions = IMPACT_CATEGORY_OPTIONS.map((option) => {
    if (option.value === ALL_CATEGORIES_VALUE)
      return { ...option, isDisabled: false };
    if (option.value === ALL_CARBON_VALUE) {
      return { ...option, isDisabled: !isAllCarbonAvailable };
    }
    return {
      ...option,
      isDisabled: !availableCategories.includes(
        option.value as CategoryFilterValue
      ),
    };
  });

  const locationOptions = [
    {
      value: ALL_LOCATIONS_VALUE,
      label: ALL_LOCATIONS_LABEL,
      isDisabled: false,
    },
    ...allLocations.map((location) => ({
      value: location,
      label: location,
      isDisabled: !availableLocations.includes(location),
    })),
  ];

  const getCategorySelectValue = () => {
    if (selectedImpactTypes.length === 0) return ALL_CATEGORIES_VALUE;
    if (selectedImpactTypes.length === 1) return selectedImpactTypes[0];
    if (isAllCarbonSelected(selectedImpactTypes)) return ALL_CARBON_VALUE;
    return `(${selectedImpactTypes.length} selected)`;
  };

  const hasActiveFilters =
    selectedImpactTypes.length > 0 || selectedLocations.length > 0;

  const getFilterChips = (): Array<{
    key: string;
    label: string;
    testId: string;
    filter: ImpactShopFilter | ImpactShopFilter[];
  }> => {
    const locationChips = selectedLocations.map((location) => ({
      key: `location-${location}`,
      label: location,
      testId: "filter-chip-location",
      filter: {
        type: ImpactShopFilterType.LOCATION,
        value: location,
      } as ImpactShopFilter,
    }));

    const categoryChips = isAllCarbonSelected(selectedImpactTypes)
      ? (() => {
          const allCarbonOption = IMPACT_CATEGORY_OPTIONS.find(
            (opt) => opt.value === ALL_CARBON_VALUE
          );
          return allCarbonOption
            ? [
                {
                  key: "impactType-all-carbon",
                  label: allCarbonOption.label,
                  testId: "filter-chip-category",
                  filter: [
                    {
                      type: ImpactShopFilterType.IMPACT_TYPE,
                      value: ImpactType.CARBON_OFFSETS,
                    },
                    {
                      type: ImpactShopFilterType.IMPACT_TYPE,
                      value: ImpactType.CARBON_REMOVAL,
                    },
                  ] as ImpactShopFilter[],
                },
              ]
            : [];
        })()
      : selectedImpactTypes
          .map((impactType) => {
            const option = categoryOptions.find(
              (opt) => opt.value === impactType
            );
            return option
              ? {
                  key: `impactType-${impactType}`,
                  label: option.label,
                  testId: "filter-chip-category",
                  filter: {
                    type: ImpactShopFilterType.IMPACT_TYPE,
                    value: impactType,
                  } as ImpactShopFilter,
                }
              : null;
          })
          .filter((chip): chip is NonNullable<typeof chip> => chip !== null);

    return [...locationChips, ...categoryChips];
  };

  return (
    <>
      <FilterBar hasActiveFilters={hasActiveFilters}>
        <FilterGroup>
          <Label use="body2">Filter by:</Label>
          <Select
            name="impact-shop-location-filter"
            label="Filter by location"
            hiddenLabel
            items={locationOptions}
            value={selectedLocations[0] || ALL_LOCATIONS_VALUE}
            onChange={(e) => onLocationChange(e.target.value)}
            data-testid="location-filter-dropdown"
          />
          <Select
            name="impact-shop-category-filter"
            label="Filter by project category"
            hiddenLabel
            items={categoryOptions}
            value={getCategorySelectValue()}
            onChange={(e) => onCategoryChange(e.target.value)}
            data-testid="category-filter-dropdown"
          />
        </FilterGroup>
        <SortWrapper>
          <Label use="body2">Sort by:</Label>
          <Select
            name="impact-shop-sort"
            label="Sort by"
            hiddenLabel
            items={[...SORT_OPTIONS]}
            value={sortOption}
            onChange={(e) =>
              setSortOption(e.target.value as ImpactShopSortOption)
            }
            data-testid="sort-by-dropdown"
          />
        </SortWrapper>
      </FilterBar>
      {hasActiveFilters && (
        <ActiveFiltersContainer>
          <ActiveFiltersLabel use="body2">Selected filters:</ActiveFiltersLabel>
          {getFilterChips().map((chip) => (
            <FilterChip key={chip.key} data-testid={chip.testId}>
              <ChipText use="body2">{chip.label}</ChipText>
              <RemoveButton
                onClick={() => removeFilter(chip.filter)}
                aria-label={`Remove ${chip.label} filter`}
              >
                <CloseIcon />
              </RemoveButton>
            </FilterChip>
          ))}
          <ClearAllButton
            onClick={clearAllFilters}
            aria-label="Clear all filters"
          >
            Clear all
          </ClearAllButton>
        </ActiveFiltersContainer>
      )}
    </>
  );
};

function isAllCarbonSelected(categoryFilters: CategoryFilterValue[]): boolean {
  return (
    categoryFilters.length === 2 &&
    categoryFilters.includes(ImpactType.CARBON_OFFSETS) &&
    categoryFilters.includes(ImpactType.CARBON_REMOVAL)
  );
}
